import { Text } from '@common/ui'
import { com, Nullable } from '@eidu/web'
import { fieldValueToString } from '../../../../../domain/entity/DraftFieldValue'
import { EntitySnapshotDiff, FieldChange, LinkChange } from './entitySnapshotDiff'
import EntityType = com.eidu.entity.type.EntityType
import FieldId = com.eidu.entity.field.FieldId
import FieldValue = com.eidu.entity.field.FieldValue

type EntityDiffSummaryProps = {
  diff: EntitySnapshotDiff
  entityType?: EntityType
  beforeLabel?: string
  afterLabel?: string
}

const formatField = (value: Nullable<FieldValue> | undefined): string => {
  if (value === undefined) return '∅'
  return fieldValueToString(value) || '""'
}

const formatLink = (value: string | undefined): string => value ?? '∅'

const fieldName = (fieldId: FieldId, entityType?: EntityType): string => {
  const idString = fieldId.toString()
  if (!entityType) return idString
  const fieldDef = entityType.fields.asJsReadonlyArrayView().find((f) => f.id.toString() === idString)
  return fieldDef?.name ?? idString
}

const border = '1px solid var(--trellis-color-outline)'

const diffCellStyle = (background: string): React.CSSProperties => ({
  flex: 1,
  paddingInline: 12,
  paddingBlock: 8,
  backgroundColor: background,
  color: 'var(--trellis-color-text-primary)',
  fontFamily: 'var(--trellis-font-mono)',
  fontSize: 13,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
})

const ChangeRow = ({ label, before, after }: { label: string; before: string; after: string }) => (
  <div style={{ display: 'flex', alignItems: 'stretch', borderTop: border }}>
    <div style={{ flex: '0 0 28%', paddingInline: 12, paddingBlock: 8, borderRight: border }}>
      <Text size="sm" fw={500}>
        {label}
      </Text>
    </div>
    <div style={{ ...diffCellStyle('var(--trellis-color-error-subtle)'), borderRight: border }}>{before}</div>
    <div style={diffCellStyle('var(--trellis-color-success-subtle)')}>{after}</div>
  </div>
)

const HeaderLabel = ({ children }: { children: string }) => (
  <Text size="xs" tt="uppercase" c="dimmed" lts={1}>
    {children}
  </Text>
)

const HeaderRow = ({ beforeLabel, afterLabel }: { beforeLabel: string; afterLabel: string }) => (
  <div style={{ display: 'flex', alignItems: 'stretch', backgroundColor: 'var(--trellis-color-surface-variant)' }}>
    <div style={{ flex: '0 0 28%', paddingInline: 12, paddingBlock: 6, borderRight: border }}>
      <HeaderLabel>Field / Link</HeaderLabel>
    </div>
    <div style={{ flex: 1, paddingInline: 12, paddingBlock: 6, borderRight: border }}>
      <HeaderLabel>{beforeLabel}</HeaderLabel>
    </div>
    <div style={{ flex: 1, paddingInline: 12, paddingBlock: 6 }}>
      <HeaderLabel>{afterLabel}</HeaderLabel>
    </div>
  </div>
)

const fieldRow = (change: FieldChange, entityType?: EntityType) => (
  <ChangeRow
    key={`field-${change.fieldId.toString()}`}
    label={`Field · ${fieldName(change.fieldId, entityType)}`}
    before={formatField(change.before)}
    after={formatField(change.after)}
  />
)

const linkRow = (change: LinkChange) => (
  <ChangeRow
    key={`link-${change.key.key}`}
    label={`Link · ${change.key.key}`}
    before={formatLink(change.before)}
    after={formatLink(change.after)}
  />
)

const EntityDiffSummary = ({
  diff,
  entityType,
  beforeLabel = 'Before',
  afterLabel = 'After',
}: EntityDiffSummaryProps) => (
  <div style={{ marginTop: 12, border, borderRadius: 'var(--trellis-radius-xs)', overflow: 'hidden' }}>
    <HeaderRow beforeLabel={beforeLabel} afterLabel={afterLabel} />
    {diff.equal ? (
      <div style={{ paddingInline: 12, paddingBlock: 12 }}>
        <Text size="sm" c="dimmed">
          No field or link changes.
        </Text>
      </div>
    ) : (
      <>
        {diff.fieldChanges.map((c) => fieldRow(c, entityType))}
        {diff.linkChanges.map((c) => linkRow(c))}
      </>
    )}
  </div>
)

export default EntityDiffSummary
